/* ------------------------------------------------------------------------
Copyright (c) 2016, VoiceVibes, Inc

VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
All contents of this file are proprietary and confidential. No part of
any of the contents of this file may be disclosed in any manner to
a third party without the prior written consent of VoiceVibes, Inc.
Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div
    v-if="results && results.scores"
    class="score-container svg-container"
    tabindex="-1"
    v-html="wordspottingSvg"
  />
</template>

<script>
import _ from 'lodash';
import ChartHelper from '../utils/ChartHelper';

export default {
  mixins: [ChartHelper],
  props: ['recording', 'wavesurfer', 'speakerNum'],
  data() {
    return {
      metric: 'wordSpotting',
      wordIds: [],
    };
  },
  computed: {
    wordChartSeries() {
      // Be sure not to mutate the scores
      const wordSpotting = this._.cloneDeep(this.results.scores.wordSpotting);

      let words = _.filter(_.map(wordSpotting, (count, word) => {
        return {word, count};
      }), word => word.count > 0);

      const centerX = 220;
      const centerY = 200;
      const innerRadius = 50;
      const outerRadius = 80;
      const markerRadius = 110;
      const minLabelAngle = 15;

      const sortedWords = _.sortBy(words, word => word.count - word.word.length / 100);
      const totalCount = _.reduce(words, (sum, word) => sum + word.count, 0);
      let angleEnd = 0;
      let prevMarkerAngle = -90;

      return _.map(sortedWords, (word, index) => {
        const angleStart = angleEnd;
        angleEnd += word.count / totalCount * 360;

        const colorIndex = sortedWords.length - index - 1;
        let color = colorIndex % 3 + 1;
        if (words.length > 1 && words.length % 3 === 1 && index === 0) {
          color = 2; // so we don't have 2 colors next to each other at top of chart
        }

        const angleMid = (angleEnd + angleStart) / 2;
        const markerAngle = Math.max(angleMid, prevMarkerAngle + minLabelAngle);
        const countMarker = this._arcPoint(centerX, centerY, markerRadius, markerAngle - 270);
        countMarker.value = word.count;
        const wordMarker = this._arcPoint(centerX, centerY, markerRadius, markerAngle - 270);
        wordMarker.x -= Math.sin(Math.PI*markerAngle/180) * 20;
        wordMarker.value = word.word;
        wordMarker.anchor = markerAngle > 180 ? 'start' : 'end';
        wordMarker.shift = Math.cos(Math.PI*markerAngle/180) * 2;
        // hacky patch to prevent overlap (Math.sin works fine except it does not push enough if many words)
        if (index === 0 && angleEnd < minLabelAngle) {
          wordMarker.shift += 1;
          wordMarker.x += 10;
        }

        const markerLineRadius = outerRadius + (words.length - index - 1)*2;
        const markerLinePath = markerAngle != angleMid
          ?  `
              M ${this._arcPathPoint(centerX, centerY, outerRadius, angleMid - 270)}
              L ${this._arcPathPoint(centerX, centerY, markerLineRadius, angleMid - 270)}
              A ${markerLineRadius} ${markerLineRadius} 0 ${Math.abs(markerAngle - angleMid) > 180 ? 1 : 0} 0 ${this._arcPathPoint(centerX, centerY, markerLineRadius, markerAngle - 270)}
              L ${this._arcPathPoint(centerX, centerY, markerRadius, markerAngle - 270)}`
          : null;

        prevMarkerAngle = markerAngle;
        return {
          path: this._arcPath(centerX, centerY, innerRadius, outerRadius, angleEnd - 270, angleStart - 270),
          pathClass: `word-arc-${color}`,
          countMarker,
          wordMarker,
          word: word.word,
          markerLinePath
        };
      });
    },

    wordspottingSvg() {
      let svg = `<svg viewBox="0 0 440 400" class="word-chart">
        <g class="word-chart-title">`;

      if (this.wordChartSeries.length == 0) {
        svg += `<text transform="translate(220,210)" text-anchor="middle" fill="#333" style="font-weight: 700; color: #333; font-size: 20px;">
                  <tspan x="0" y="-15">WORD</tspan>
                  <tspan x="0" dy="20">SPOTTING</tspan>
                </text>
                <text transform="translate(220,210)" text-anchor="middle" fill="#333" style="font-weight: 700; color: #333; font-size: 14px;">
                  <tspan x="0" y="35">No unwanted words</tspan>
                  <tspan x="0" y="50">were spoken!</tspan>
                </text>`;
      } else {
        svg += `<text transform="translate(220,210)" text-anchor="middle" fill="#333" style="font-weight: 700; color: #333; font-size: 16px;">
                  <tspan x="0" y="-15">WORD</tspan>
                  <tspan x="0" dy="20">SPOTTING</tspan>
                </text>`;
      }

      svg += `<circle cx="220" cy="200" r="110" fill="none" stroke="#ccc" stroke-width="1" stroke-dasharray="4"></circle>
        </g>`;

      this.wordIds = [];

      _.each(this.wordChartSeries, (_item) => {
        const item = _.cloneDeep(_item);

        const wordMarkerDy = (0.5 - item.wordMarker.shift) + 'ex',
          wordId = 'wordSpotting_' + item.word.replace(/[^a-zA-Z]/g, ''),
          hotSpotHandler = () => {
            this._showHotspots(this.metric, item.word);
            return false;
          };

        this.wordIds.push({wordId, hotSpotHandler}); // save for later so we can register the events

        svg += `<g>
          <path d="${item.path}" fill="#000" class="word-arc ${item.pathClass}"></path>`;
        svg += (item.markerLinePath) ?
          `<path v-if="" d="${item.markerLinePath}" fill="none" stroke="#ccc"></path>` : '';
        svg += `<g id="${wordId}1" class="word-count-marker" style="cursor:pointer;">
          <circle cx="${item.countMarker.x}" cy="${item.countMarker.y}" r="14" fill="none" stroke="#999"></circle>
          <circle cx="${item.countMarker.x}" cy="${item.countMarker.y}" r="14" fill="#fff"></circle>
          <text x="${item.countMarker.x}" y="${item.countMarker.y}" font-size="16"
                text-anchor="middle" dy=".6ex">
            ${item.countMarker.value}
          </text>
        </g>
        <g id="${wordId}2" class="word-word-marker" style="cursor:pointer;">
          <text x="${item.wordMarker.x}" y="${item.wordMarker.y}" font-size="18"
                text-anchor="${item.wordMarker.anchor}" dy="${wordMarkerDy}">
            ${item.wordMarker.value}
          </text>
        </g>
      </g>`;
      });

      svg += '</svg>';

      return svg;
    }
  },
  mounted() {
    this.wordIds.forEach((obj) => {
      this._addEvent(`${obj.wordId}1`, 'click', obj.hotSpotHandler);
      this._addEvent(`${obj.wordId}2`, 'click', obj.hotSpotHandler);
    });
  },
};
</script>
